import React, {useState, useRef} from "react";
import {Upload, X, Image as ImageIcon} from "lucide-react";
import {Button} from "../ui";
import {usePropertyTypeIcon} from "../../hooks/usePropertyTypeIcon";

interface PropertyTypeIconUploadProps {
  propertyTypeId?: number;
  currentIcon?: string;
  onIconChange?: (iconUrl: string | null) => void;
  disabled?: boolean;
}

export const PropertyTypeIconUpload: React.FC<PropertyTypeIconUploadProps> = ({
  propertyTypeId,
  currentIcon,
  onIconChange,
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(currentIcon || null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {uploadIcon, deleteIcon, isUploading, isDeleting} = usePropertyTypeIcon();

  const handleFileSelect = (file: File) => {
    if (disabled) return;

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload if propertyTypeId is provided
    if (propertyTypeId) {
      uploadIcon(propertyTypeId, file)
        .then((result) => {
          if (result?.data?.icon) {
            setPreview(result.data.icon);
            onIconChange?.(result.data.icon);
          }
        })
        .catch(() => {
          setPreview(currentIcon || null);
        });
    } else {
      // For create mode, just set preview
      onIconChange?.(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileSelect(imageFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveIcon = async () => {
    if (disabled) return;

    if (propertyTypeId) {
      try {
        await deleteIcon(propertyTypeId);
        setPreview(null);
        onIconChange?.(null);
      } catch (error) {
        // Error handling is done in the hook
      }
    } else {
      setPreview(null);
      onIconChange?.(null);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Property Type Icon
      </label>
      
      <div className="flex items-start space-x-4">
        {/* Upload Area */}
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-colors duration-200 w-32 h-32 flex items-center justify-center
            ${isDragOver
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
        >
          {preview ? (
            <img
              src={preview}
              alt="Property type icon"
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="flex flex-col items-center">
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Click to upload</p>
            </div>
          )}
          
          {(isUploading || isDeleting) && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col space-y-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClick}
            disabled={disabled || isUploading || isDeleting}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Icon
          </Button>
          
          {preview && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveIcon}
              disabled={disabled || isUploading || isDeleting}
              className="text-red-600 hover:text-red-700"
            >
              <X className="w-4 h-4 mr-2" />
              Remove
            </Button>
          )}
        </div>
      </div>

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Help Text */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        <p>Upload an icon for this property type (JPEG, PNG, WEBP, GIF)</p>
        <p>Maximum file size: 5MB</p>
        <p>Recommended size: 64x64px or larger</p>
      </div>
    </div>
  );
};